/**
 * When may the post-first-build $1 offer appear? (PANDA-2058)
 *
 * The T7 offer is the one trial trigger the user did not ask for: it fires off a
 * timer after a project's first successful build, not off a click. That makes
 * WHEN it fires the entire design, and the first version got it wrong.
 *
 * The old rule was "10s after the first-build value line lands in chat". Two
 * things break under it:
 *
 *  1. The value line is not the preview. announceAppReady waits for the preview
 *     to paint but gives up after PREVIEW_READY_TIMEOUT_MS and posts the
 *     completion message anyway — so on a slow sandbox boot, a mobile deployed
 *     preview, or a build that ends up failing, the 10s clock was already
 *     running over an app the user could not yet see. The offer then landed on
 *     a spinner. That is not the first-value moment; it is an interruption of
 *     the wait for one.
 *
 *  2. Even when the preview HAD painted, 10s is not long enough to look at an
 *     app. The offer says "Finish {your build} for $1" — a claim that only
 *     lands once the user has seen the build it names.
 *
 * So the trigger moves from "the chat said it's ready" to "the app is on screen
 * and has been left alone for a while". Two rules, both pinned by the tests:
 *
 *   • HOLD while the app isn't there to look at — the preview hasn't painted,
 *     the first preview failed, another build/edit is running, or the app has
 *     painted onto a pane the user isn't on (the mobile default) or a browser
 *     tab they've left.
 *   • Once it is there AND being looked at, count out an UNINTERRUPTED quiet
 *     window. Anything that takes the app off screen (a follow-up build, a
 *     preview that drops out, a switch back to chat) restarts the window rather
 *     than pausing it: the point is a continuous stretch of the user and their
 *     app, not an accumulated total.
 *
 * The offer still reaches high-intent moments immediately — a credit/plan block
 * routes to the $1 trial through resolveCreditGateSurface (lib/trial/
 * gate-surface.ts), which does not wait on any of this. This module only governs
 * the UNSOLICITED timer, and the credit gate settles it on the way past so one
 * build can never produce two asks.
 *
 * Pure and DOM-free on purpose. The scheduling itself is four lines of
 * setTimeout in app/project/[id]/page.tsx; the policy is here so the acceptance
 * criteria are enforced by tests instead of by reading a 30k-line component.
 */

/**
 * How long the app must sit on screen, undisturbed, before the offer appears.
 *
 * The ticket asks for "around 30-40 seconds". 35s is the middle of that: long
 * enough to open a page or two of the generated app and form an opinion about
 * it, short enough that the offer still reads as part of the build moment
 * rather than as an ad that arrived later.
 */
export const FIRST_BUILD_OFFER_QUIET_WINDOW_MS = 35_000;

/** Bounds for the env override below. Anything outside them is a typo, not a
 *  configuration: 5s is back to interrupting, 5min is a different feature. */
const MIN_QUIET_WINDOW_MS = 10_000;
const MAX_QUIET_WINDOW_MS = 300_000;

/**
 * The quiet window, with an env override for tuning the 30-40s band without a
 * code change.
 *
 * Read at CALL time rather than frozen at module-eval, matching holdoutPercent
 * in lib/trial/config.ts — a knob that can only be exercised by module-registry
 * surgery is a knob nobody tests. Note the static `process.env.NEXT_PUBLIC_…`
 * reference: Next inlines these at build time only when they are written out in
 * full, so this must never be rewritten as a dynamic lookup.
 */
export function firstBuildOfferQuietWindowMs(): number {
  const raw = Number(
    process.env.NEXT_PUBLIC_TRIAL_FIRST_BUILD_DELAY_MS ??
      FIRST_BUILD_OFFER_QUIET_WINDOW_MS,
  );
  if (!Number.isFinite(raw)) return FIRST_BUILD_OFFER_QUIET_WINDOW_MS;
  return Math.max(MIN_QUIET_WINDOW_MS, Math.min(MAX_QUIET_WINDOW_MS, raw));
}

/**
 * Why the quiet window cannot be running right now.
 *
 * Named rather than boolean because these are the reasons a QA report will ask
 * about ("the offer never appeared") and they are logged verbatim.
 */
export type FirstBuildOfferHold =
  /** The build finished but the app hasn't painted yet. */
  | "preview_pending"
  /**
   * The app has painted, but onto a pane the user is not looking at.
   *
   * This is the one that made the first version of the delay wrong in practice.
   * On mobile the workspace opens on the CHAT view and the preview lives behind
   * a tab, so `previewReady` goes true while the user is still reading the chat.
   * Counting a "time with your app" window from that point meant the window was
   * most of the way through before they had tapped over — measured at roughly
   * 18 of an intended 35 seconds. Ready is not the same as seen.
   */
  | "preview_hidden"
  /** The first preview never arrived (terminal failure / refused preview).
   *  There is no first-value moment to follow, so there is nothing to sell. */
  | "preview_failed"
  /** A build or edit is in flight. The user is working, not inspecting. */
  | "building";

export interface FirstBuildOfferSignals {
  /** The preview iframe has painted the built app (project page `previewReady`:
   *  iframe loaded + boot overlay gone + no unrecovered build error + not
   *  mid-fix). Says the app EXISTS on screen, not that anyone is looking. */
  previewReady: boolean;
  /**
   * The preview pane is the one actually on screen — the Preview right-tab on
   * desktop, the preview view on mobile — AND the browser tab itself is in the
   * foreground. Both halves are "is the user looking at this?", so they are one
   * signal here; the hook composes them.
   */
  previewOnScreen: boolean;
  /** This project's first preview ended in a failure it never recovered from. */
  previewFailed: boolean;
  /** A generation is running right now. */
  building: boolean;
}

/**
 * The one reason the window is held, or null when it is free to run.
 *
 * Order matters for the LOG, not the outcome: a failed first preview is the
 * most specific answer to "why didn't the offer show?", so it is reported ahead
 * of the plain "no preview yet" that it also implies.
 */
export function firstBuildOfferHold(
  signals: FirstBuildOfferSignals,
): FirstBuildOfferHold | null {
  if (signals.previewFailed) return "preview_failed";
  if (signals.building) return "building";
  if (!signals.previewReady) return "preview_pending";
  // Checked LAST, so a hidden pane is only reported once there is something on
  // it worth looking at — "preview_hidden" during a build would be true and
  // useless.
  if (!signals.previewOnScreen) return "preview_hidden";
  return null;
}

/**
 * How many times the offer may step aside for a modal that is already up before
 * it gives up for good.
 *
 * The window can elapse while some other surface owns the screen (a paywall the
 * user opened, the build-quality survey, a modal dismissed seconds ago). The
 * old code DROPPED the offer in that case, which quietly cost the exposure. It
 * now waits another window instead — but not forever: a user who keeps a modal
 * on screen for four windows running is not waiting for this one.
 */
export const MAX_FIRST_BUILD_OFFER_DEFERRALS = 4;

export function canDeferFirstBuildOffer(deferrals: number): boolean {
  return deferrals < MAX_FIRST_BUILD_OFFER_DEFERRALS;
}

/**
 * Timing attached to every trial_offer_* / trial_eligibility_evaluated row this
 * trigger produces (the ticket's "track timing so we can compare conversion
 * against immediate-show behavior").
 *
 * `variant` is what makes the comparison possible: rows written before this
 * change carry no timing at all, so "no variant" IS the immediate-show cohort
 * and the two are separable in one breakdown without back-filling anything.
 */
export interface FirstBuildOfferTiming {
  /** Which timing rule produced this exposure. */
  variant: "first_value_delay";
  /** The configured quiet window, so a tuned deployment is separable. */
  quietWindowMs: number;
  /** Time from the app first appearing on screen to the offer. This is the
   *  number the ticket cares about — how long the user actually got. */
  msSincePreviewReady: number | null;
  /** Time from the first-build value moment to the offer. Larger than the
   *  above by however long the preview took to paint. */
  msSinceFirstBuild: number | null;
  /** How many windows were restarted because another modal held the screen. */
  deferrals: number;
  /**
   * WHICH of the user's projects this build belongs to — 1 for their first, 2
   * for their second, and so on. Null when it could not be read.
   *
   * Carried because the offer's whole failure mode is being scoped to the first
   * project by accident: a latch that outlives the project it was set for, a
   * dismissal on project 1 that silences project 2. Without the ordinal on the
   * row, "does this fire on second and later projects?" cannot be asked of the
   * funnel at all — which is why the last one went unnoticed.
   */
  projectOrdinal: number | null;
  /** When the app first became visible and stable (ISO). The anchor every
   *  duration here is measured from, reported absolutely so an exposure can be
   *  lined up against the build's own server-side timeline. */
  previewReadyAt: string | null;
  /** Did the uninterrupted quiet window actually elapse? True on every exposure
   *  this trigger produces; false on a suppression that never got that far. */
  delayCompleted: boolean;
}

export interface FirstBuildOfferTimingInput {
  /** When the first-build value moment latched (ms epoch). */
  latchedAt: number | null;
  /** When the preview first became visible and stable (ms epoch). */
  previewReadyAt: number | null;
  /** When the offer fired (ms epoch). */
  firedAt: number;
  deferrals: number;
  quietWindowMs: number;
  /** The user's project ordinal for this build (1-based). Null when unknown. */
  projectOrdinal?: number | null;
  /** Did the quiet window run to completion? Defaults to true — the only
   *  caller that passes false is a suppression, which never fires an offer. */
  delayCompleted?: boolean;
}

/** Non-negative elapsed ms, or null when the anchor was never recorded. A
 *  clock that went backwards must not emit a negative duration. */
function elapsed(from: number | null, to: number): number | null {
  if (from === null || !Number.isFinite(from)) return null;
  return Math.max(0, to - from);
}

/** A ms-epoch anchor as an ISO instant, or null when it was never recorded.
 *  Never throws on a bad number — an unparseable anchor is simply unknown. */
function isoOrNull(at: number | null): string | null {
  if (at === null || !Number.isFinite(at)) return null;
  try {
    return new Date(at).toISOString();
  } catch {
    return null;
  }
}

export function describeFirstBuildOfferTiming(
  input: FirstBuildOfferTimingInput,
): FirstBuildOfferTiming {
  return {
    variant: "first_value_delay",
    quietWindowMs: input.quietWindowMs,
    msSincePreviewReady: elapsed(input.previewReadyAt, input.firedAt),
    msSinceFirstBuild: elapsed(input.latchedAt, input.firedAt),
    deferrals: input.deferrals,
    projectOrdinal:
      typeof input.projectOrdinal === "number" &&
      Number.isFinite(input.projectOrdinal)
        ? input.projectOrdinal
        : null,
    previewReadyAt: isoOrNull(input.previewReadyAt),
    delayCompleted: input.delayCompleted !== false,
  };
}

/**
 * Flatten the timing into event properties.
 *
 * Owned here rather than in lib/analytics.ts so the property NAMES sit beside
 * the fields they come from and cannot drift apart — the shape travels through
 * four hops (project page → openUpgradeEntry → the CustomEvent → the modal)
 * before anything is captured.
 */
export function firstBuildOfferTimingProps(
  timing: FirstBuildOfferTiming | null | undefined,
): Record<string, unknown> {
  if (!timing) return {};
  return {
    offer_timing_variant: timing.variant,
    offer_quiet_window_ms: timing.quietWindowMs,
    offer_ms_since_preview_ready: timing.msSincePreviewReady,
    offer_ms_since_first_build: timing.msSinceFirstBuild,
    offer_deferrals: timing.deferrals,
    offer_project_ordinal: timing.projectOrdinal,
    offer_preview_ready_at: timing.previewReadyAt,
    offer_delay_completed: timing.delayCompleted,
  };
}

/**
 * Why an offer this trigger had already decided to make never reached the user.
 *
 * Every one of these used to be silent — a `console.info` at best — which is
 * how the offer came to be first-project-only without anyone noticing: the
 * decision row said `trial_offer_shown: true` (the dispatch went out) while the
 * modal host dropped it on the floor. These are the reasons that row is a lie,
 * named so the funnel can report them (trackTrialOfferSuppressed).
 */
export type FirstBuildOfferSuppression =
  /** Stood aside for another modal until the deferral budget ran out. */
  | "deferral_limit"
  /**
   * The user already closed the $1 offer for THIS project, so the timer must
   * not re-show it. Scoped to the project on purpose: the same latch applied
   * session-wide meant one dismissal on a first project silenced the offer for
   * every project built afterwards.
   */
  | "already_dismissed"
  /** The credit gate made the ask first — the high-intent path won, and one
   *  build must never produce two asks. Not a loss; recorded for completeness. */
  | "credit_gate_trial";
